import { AlarmClock, History, Info, Settings } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { Menu } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { maintenanceItemsApi } from '../buildingManagementApi';
import type { BuildingMaintenanceSettingsDetail } from '../buildingManagementTypes';
import { AgingSettingsModal } from '../components/AgingSettingsModal';

type TabKey = 'info';

const BREADCRUMB: BreadcrumbSegment[] = [
  'General Services',
  'Building Management',
  { label: 'Settings', to: '/general-services/building-management/settings' },
  'Aging',
];

/**
 * The two thresholds every item is aged against — one settings row, read here and edited through
 * its own card.
 *
 * **Readable on `building-management.read`, editable on `configure-aging`** — anyone monitoring
 * the register is entitled to know what "Near Expiration" means there; changing it moves every
 * item between bands at once, so the Edit carries the narrower key and hides without it.
 */
export function AgingSettingsPage() {
  const [settings, setSettings] = useState<BuildingMaintenanceSettingsDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback((signal?: AbortSignal) => {
    maintenanceItemsApi
      .settings(signal)
      .then((next) => {
        if (signal?.aborted) return;
        setSettings(next);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (signal?.aborted) return;
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the aging settings.', 0));
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  if (error && !settings) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={BREADCRUMB} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={BREADCRUMB} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full max-w-4xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={BREADCRUMB} />

      <ProfileHeaderShell
        id="aging-settings"
        header={
          <ProfileHeader
            id="aging-settings-header"
            icon={<AlarmClock className="h-6 w-6" aria-hidden="true" />}
            title="Aging"
            meta={[
              `Near Expiration at ${settings.nearExpirationDays} days`,
              `${settings.escalatedLabel} at ${settings.overdueEscalationDays} days past due`,
            ]}
            createdBy={settings.createdByName}
            createdAt={settings.createdAt}
            actions={
              <Menu
                id="aging-settings-cog"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    permission: 'audit-logs.read',
                    onClick: () => setHistoryOpen(true),
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Aging options"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          <ProfileBanner
            id="aging-settings-banner"
            tone="slate"
            icon={<Info className="h-4 w-4" aria-hidden="true" />}
            message={
              settings.updatedAt == null && settings.createdAt == null
                ? 'These are the defaults — nobody has saved the settings yet. Every item is aged against them.'
                : 'Every item in the register is aged against these numbers, re-read on every list, calendar and profile.'
            }
          />
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="aging-settings-thresholds"
          title="Thresholds"
          onEdit={() => setEditing(true)}
          editPermission="building-management.configure-aging"
        >
          <PreviewTable
            variant="table-display"
            rows={[
              {
                label: 'Near Expiration window',
                value: `${settings.nearExpirationDays} days before the due date, for a category that sets no window of its own`,
              },
              {
                label: 'Expired',
                value: 'From the day after the due date',
              },
              {
                label: settings.escalatedLabel,
                value: `${settings.overdueEscalationDays} days or more past the due date`,
              },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="aging-settings-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: settings.createdByName },
              { label: 'Created', value: settings.createdAt ? formatDateTime(settings.createdAt) : null, mono: true },
              { label: 'Updated by', value: settings.updatedByName },
              { label: 'Updated', value: settings.updatedAt ? formatDateTime(settings.updatedAt) : null, mono: true },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <AgingSettingsModal
          open
          current={settings}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setEditing(false);
            setSettings(next);
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="building-management"
        entityTypes={['BuildingMaintenanceSettings']}
      />
    </div>
  );
}
